/**
 * Warden kit: one hand-authored clip per ability (sword in the right hand, round shield strapped
 * on the left forearm). Palette: steel-silver blade arcs, warm gold on the big hits, a softer
 * holy-gold sheen on the defensives.
 *
 * Silhouettes (each ability reads differently from the game camera):
 *   Shield Slam        coil right, then a stepping lunge with the shield punched straight out
 *   Cleave Strike      crouch and coil left, one full clockwise spin with the sword arm flung out
 *   Bulwark Strike     deep crouch with the blade low behind, then a rising diagonal slash with a hop
 *   Consecrated Ground hop with the sword raised overhead, then a kneeling plunge into the floor
 *   Last Stand         wide braced crouch, shield up across the chest, head tucked
 *   Shield Wall Bash   shield hoisted overhead, then shoved forward with a step
 *   Shield Bash        a 0.1 s jab: the fastest motion in the kit
 *   Taunt              chest out, sword pointed at the target, head thrown back in a roar
 *   Grip               reach out with the shield hand, then yank back with a step away
 *   Aegis Throw        backhand discus throw: shield arm wrapped across the chest, whipped out wide
 *   Aegis Recall       open hand flung out to call the shield, then a catch that shoves the body back
 *   Riposte            shield flicked up to parry, then one flat wide sweep across the cone (no turn)
 *
 * All root motion is visual only and returns to zero by the end of each clip. Spins end on whole
 * turns. Everything here is built once at module load; nothing allocates at runtime.
 */
import { ABILITY } from '@keystone/content';
import { type Clip, defineClip } from '../clips';
import { registerAbilityClips } from '../registry';

// Palette (sRGB).
const STEEL = '#e6edf7';
const STEEL_TAIL = '#5d6a80';
const GOLD = '#ffc24a';
const GOLD_TAIL = '#7a4a08';
const HOLY = '#fff0b8';
const HOLY_TAIL = '#b8862a';
const WHITE = '#ffffff';
const ROAR = '#ffb347';
const ROAR_TAIL = '#8a3a0a';
const CHAIN = '#d9a441';
const CHAIN_TAIL = '#3a2a4a';

/** Shield Slam: coil the shield back, then a hard stepping punch with the shield face. */
export const shieldSlam = defineClip({
  id: 'warden.shield_slam',
  duration: 0.56,
  blendIn: 0.04,
  keys: [
    // Anticipation: torso coils right, shield drawn back to the hip, weight on the back foot.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [0.08, -0.5, 0],
        chest: [0, -0.2, 0],
        head: [0, 0.45, 0],
        leftUpperArm: [-0.1, -0.15, 0.55],
        leftLowerArm: [1.3],
        rightUpperArm: [0.35, -0.1, 0.35],
        rightLowerArm: [0.6],
        leftUpperLeg: [0.25, 0, 0.08],
        leftLowerLeg: [0.4],
        rightLowerLeg: [0.45],
      },
      root: { lunge: -0.1, hop: -0.03 },
    },
    // Impact: torso unwinds left, shield punched straight out at chest height, long stride.
    {
      t: 0.2,
      ease: 'snap',
      hold: 0.07,
      pose: {
        spine: [0.2, 0.55, 0],
        chest: [0.05, 0.2, 0],
        head: [-0.05, -0.45, 0],
        leftUpperArm: [1.15, 0.65, 0],
        leftLowerArm: [0],
        rightUpperArm: [-0.2, -0.1, 0.5],
        rightLowerArm: [0.8],
        leftUpperLeg: [0.6, 0, 0.05],
        leftLowerLeg: [0.45],
        rightUpperLeg: [-0.35, 0, 0.05],
        rightLowerLeg: [0.25],
      },
      root: { lunge: 0.42, hop: -0.04 },
    },
    { t: 0.56, ease: 'out' },
  ],
  trails: [
    { from: 0.1, to: 0.27, hand: 'left', color: GOLD, tail: GOLD_TAIL, style: 'crescent', width: 1, fade: 0.2, alpha: 0.85 },
  ],
  cues: [
    { t: 0.2, fx: 'impact', at: 'hand', hand: 'left', color: GOLD, scale: 1.1 },
    { t: 0.2, fx: 'flash', at: 'front', color: HOLY, scale: 0.7 },
    { t: 0.21, fx: 'shockwave', at: 'front', color: GOLD, scale: 0.9 },
    { t: 0.22, fx: 'dust', at: 'ground', scale: 0.5 },
  ],
  hitStop: { t: 0.2, ms: 60, shake: 0.22 },
});

/** Cleave Strike: crouch and coil left, then one full clockwise spin with the sword flung wide. */
export const cleaveStrike = defineClip({
  id: 'warden.cleave_strike',
  duration: 0.6,
  blendIn: 0.05,
  keys: [
    // Coil: sword drawn across the body to the left hip, shoulders turned away, knees bent.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [0.15, 0.55, 0],
        chest: [0.05, 0.2, 0],
        head: [0, -0.5, 0],
        rightUpperArm: [0.7, 1.0, 0.1],
        rightLowerArm: [0.9],
        leftUpperArm: [0.4, -0.2, 0.45],
        leftLowerArm: [1.1],
        leftUpperLeg: [0.35, 0, 0.2],
        rightUpperLeg: [0.35, 0, 0.2],
        leftLowerLeg: [0.6],
        rightLowerLeg: [0.6],
      },
      root: { hop: -0.06, spin: 0.08 },
    },
    // Mid-spin: sword arm straight out to the side, the shield arm tucked in for speed.
    {
      t: 0.24,
      ease: 'in',
      pose: {
        spine: [0.12, -0.15, 0.08],
        head: [0, 0.2, 0],
        rightUpperArm: [0.45, 0.05, 1.25],
        rightLowerArm: [0.05],
        leftUpperArm: [0.6, 0.6, 0.2],
        leftLowerArm: [1.3],
        leftUpperLeg: [0.25, 0, 0.25],
        rightUpperLeg: [0.25, 0, 0.25],
        leftLowerLeg: [0.45],
        rightLowerLeg: [0.45],
      },
      root: { hop: 0.06, spin: -0.55, lunge: 0.1 },
    },
    // Impact: the sweep finishes in front, blade swung through to the right, short freeze.
    {
      t: 0.33,
      ease: 'back',
      overshoot: 1.1,
      hold: 0.05,
      pose: {
        spine: [0.25, -0.45, 0],
        chest: [0.05, -0.15, 0],
        head: [-0.05, 0.4, 0],
        rightUpperArm: [0.85, -0.25, 1.0],
        rightLowerArm: [0.05],
        leftUpperArm: [0.7, 0.5, 0.25],
        leftLowerArm: [1.2],
        leftUpperLeg: [0.45, 0, 0.2],
        rightUpperLeg: [0.1, 0, 0.25],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.4],
      },
      root: { spin: -1, lunge: 0.2, hop: -0.05 },
    },
    { t: 0.6, ease: 'out' },
  ],
  trails: [
    { from: 0.1, to: 0.4, hand: 'right', color: STEEL, tail: GOLD, style: 'crescent', width: 0.95, fade: 0.24, alpha: 0.85 },
  ],
  cues: [
    { t: 0.33, fx: 'slash', at: 'tip', color: STEEL, scale: 1.2 },
    { t: 0.33, fx: 'sparks', at: 'tip', color: GOLD, scale: 0.7 },
    { t: 0.34, fx: 'dust', at: 'ground', scale: 0.7 },
  ],
  hitStop: { t: 0.33, ms: 45, shake: 0.14 },
});

/** Bulwark Strike: sink low with the blade behind, then a rising diagonal slash with a small hop. */
export const bulwarkStrike = defineClip({
  id: 'warden.bulwark_strike',
  duration: 0.62,
  blendIn: 0.05,
  keys: [
    // Wind-up: deep crouch, torso turned right, sword trailing low behind the right hip.
    {
      t: 0.16,
      ease: 'out',
      pose: {
        spine: [0.3, -0.5, 0.1],
        chest: [0.1, -0.2, 0],
        head: [-0.15, 0.45, 0],
        rightUpperArm: [-0.45, -0.3, 0.35],
        rightLowerArm: [0.3],
        leftUpperArm: [1.0, 0.7, 0.2],
        leftLowerArm: [1.1],
        leftUpperLeg: [0.6, 0, 0.15],
        rightUpperLeg: [0.25, 0, 0.15],
        leftLowerLeg: [0.95],
        rightLowerLeg: [0.9],
      },
      root: { hop: -0.08, lunge: -0.06 },
    },
    // Impact: the blade rips up across the body to high left, chest open, up on the toes.
    {
      t: 0.25,
      ease: 'back',
      overshoot: 1.3,
      hold: 0.08,
      pose: {
        spine: [-0.18, 0.5, -0.1],
        chest: [-0.1, 0.2, 0],
        head: [0.1, -0.4, 0],
        rightUpperArm: [2.3, 0.75, 0.35],
        rightLowerArm: [0.1],
        leftUpperArm: [0.3, -0.1, 0.6],
        leftLowerArm: [0.7],
        leftUpperLeg: [0.35, 0, 0.05],
        rightUpperLeg: [-0.25, 0, 0.05],
        leftLowerLeg: [0.15],
        rightLowerLeg: [0.3],
        rightFoot: [0.3],
      },
      root: { lunge: 0.3, hop: 0.14 },
    },
    // Landing: weight drops back, blade still high.
    {
      t: 0.42,
      ease: 'in',
      pose: {
        spine: [0.1, 0.25, 0],
        head: [0, -0.2, 0],
        rightUpperArm: [1.6, 0.5, 0.3],
        rightLowerArm: [0.4],
        leftUpperArm: [0.5, 0.2, 0.3],
        leftLowerArm: [0.9],
        leftUpperLeg: [0.3, 0, 0.1],
        leftLowerLeg: [0.4],
        rightLowerLeg: [0.35],
      },
      root: { lunge: 0.22, hop: -0.03 },
    },
    { t: 0.62, ease: 'out' },
  ],
  trails: [
    { from: 0.14, to: 0.34, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'crescent', width: 1, fade: 0.26, alpha: 0.9 },
    // Holy sheen riding the same arc (the strike heals).
    { from: 0.17, to: 0.36, hand: 'right', color: HOLY, tail: HOLY_TAIL, style: 'holy', width: 0.6, fade: 0.32, alpha: 0.5 },
  ],
  cues: [
    { t: 0.25, fx: 'slash', at: 'tip', color: GOLD, scale: 1.3 },
    { t: 0.25, fx: 'impact', at: 'front', color: GOLD, scale: 1 },
    { t: 0.3, fx: 'holy', at: 'self', color: HOLY, scale: 0.9 },
  ],
  hitStop: { t: 0.25, ms: 70, shake: 0.26 },
});

/** Consecrated Ground: hop with the sword overhead, then a kneeling plunge into the floor. */
export const consecratedGround = defineClip({
  id: 'warden.consecrated_ground',
  duration: 0.66,
  blendIn: 0.05,
  keys: [
    // Rise: sword hoisted overhead, chest open, up on the toes.
    {
      t: 0.16,
      ease: 'out',
      pose: {
        spine: [-0.2, 0, 0],
        chest: [-0.1, 0, 0],
        head: [0.15, 0, 0],
        rightUpperArm: [2.55, 0.3, 0.1],
        rightLowerArm: [0.35],
        leftUpperArm: [0.4, -0.1, 0.7],
        leftLowerArm: [0.6],
        leftUpperLeg: [0.15],
        leftLowerLeg: [0.25],
        rightLowerLeg: [0.15],
      },
      root: { hop: 0.16, lunge: -0.04 },
    },
    // Plunge: drop to one knee, sword driven down in front, torso folded over it.
    {
      t: 0.26,
      ease: 'snap',
      hold: 0.1,
      pose: {
        spine: [0.55, 0, 0],
        chest: [0.15, 0, 0],
        head: [-0.25, 0, 0],
        rightUpperArm: [0.3, 0.45, 0.05],
        rightLowerArm: [0.15],
        // Wrist yaw tips the blade down: it is driven point-first into the floor.
        rightHand: [0, -1.7, 0],
        leftUpperArm: [0.2, -0.2, 0.85],
        leftLowerArm: [0.5],
        leftUpperLeg: [1.1, 0, 0.1],
        leftLowerLeg: [1.2],
        rightUpperLeg: [-0.1, 0, 0.1],
        rightLowerLeg: [1.4],
        rightFoot: [0.4],
      },
      root: { hop: -0.32, lunge: 0.12 },
    },
    { t: 0.66, ease: 'out' },
  ],
  trails: [
    { from: 0.12, to: 0.32, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'crescent', width: 0.9, fade: 0.22, alpha: 0.85 },
    { from: 0.12, to: 0.34, hand: 'right', color: HOLY, tail: HOLY_TAIL, style: 'holy', width: 0.5, fade: 0.3, alpha: 0.45 },
  ],
  cues: [
    { t: 0.26, fx: 'cracks', at: 'ground', color: GOLD, scale: 1 },
    { t: 0.26, fx: 'shockwave', at: 'ground', color: GOLD, scale: 1.5 },
    { t: 0.27, fx: 'holy', at: 'ground', color: HOLY, scale: 1.3 },
    { t: 0.3, fx: 'embers', at: 'ground', color: GOLD, scale: 0.8 },
  ],
  hitStop: { t: 0.26, ms: 80, shake: 0.3 },
});

/** Last Stand: a wide braced crouch behind the raised shield, then a holy-gold dome flash. */
export const lastStand = defineClip({
  id: 'warden.last_stand',
  duration: 0.66,
  blendIn: 0.04,
  keys: [
    // Brace: drop into a wide stance, shield swung up across the chest, sword pulled back low.
    {
      t: 0.14,
      ease: 'snap',
      pose: {
        spine: [0.3, 0.15, 0],
        chest: [0.1, 0.05, 0],
        neck: [0.1, 0, 0],
        head: [0.05, -0.1, 0],
        leftUpperArm: [1.1, 0.85, 0.15],
        leftLowerArm: [1.0],
        rightUpperArm: [0.1, -0.15, 0.45],
        rightLowerArm: [0.9],
        leftUpperLeg: [0.55, 0, 0.35],
        rightUpperLeg: [0.35, 0, 0.35],
        leftLowerLeg: [0.95],
        rightLowerLeg: [0.85],
      },
      root: { hop: -0.12, lunge: -0.05 },
    },
    // Hold the brace (the dome flashes here), a small shudder as it lands.
    {
      t: 0.2,
      ease: 'back',
      overshoot: 2,
      hold: 0.2,
      pose: {
        spine: [0.36, 0.15, 0],
        chest: [0.12, 0.05, 0],
        neck: [0.12, 0, 0],
        head: [0.08, -0.1, 0],
        leftUpperArm: [1.15, 0.9, 0.15],
        leftLowerArm: [1.05],
        rightUpperArm: [0.1, -0.15, 0.45],
        rightLowerArm: [0.95],
        leftUpperLeg: [0.6, 0, 0.38],
        rightUpperLeg: [0.4, 0, 0.38],
        leftLowerLeg: [1.05],
        rightLowerLeg: [0.95],
      },
      root: { hop: -0.15, lunge: -0.06 },
    },
    { t: 0.66, ease: 'out' },
  ],
  trails: [{ from: 0.02, to: 0.22, hand: 'left', color: HOLY, tail: HOLY_TAIL, style: 'holy', width: 1, fade: 0.3, alpha: 0.7 }],
  cues: [
    { t: 0.2, fx: 'flash', at: 'self', color: HOLY, scale: 1.4 },
    { t: 0.2, fx: 'holy', at: 'self', color: GOLD, scale: 1.7 },
    { t: 0.21, fx: 'shockwave', at: 'ground', color: HOLY, scale: 1.1 },
    { t: 0.22, fx: 'dust', at: 'ground', scale: 0.6 },
  ],
  hitStop: { t: 0.2, ms: 50, shake: 0.18 },
});

/** Shield Wall Bash: hoist the shield overhead, then shove it forward with a step. */
export const shieldWallBash = defineClip({
  id: 'warden.shield_wall_bash',
  duration: 0.5,
  blendIn: 0.04,
  keys: [
    // Raise: shield high in front of the face, chest leaning back, sword arm braced.
    {
      t: 0.11,
      ease: 'out',
      pose: {
        spine: [-0.12, 0.2, 0],
        chest: [-0.05, 0.1, 0],
        head: [0.05, -0.15, 0],
        leftUpperArm: [2.0, 0.55, 0.1],
        leftLowerArm: [0.9],
        rightUpperArm: [0.3, -0.1, 0.5],
        rightLowerArm: [0.9],
        leftLowerLeg: [0.25],
        rightLowerLeg: [0.3],
      },
      root: { lunge: -0.06, hop: 0.03 },
    },
    // Push: shield driven forward and down, shoulder behind it, front leg stepping in.
    {
      t: 0.2,
      ease: 'snap',
      hold: 0.06,
      pose: {
        spine: [0.28, 0.25, 0],
        chest: [0.08, 0.1, 0],
        head: [-0.12, -0.2, 0],
        leftUpperArm: [1.2, 0.7, 0],
        leftLowerArm: [0.05],
        rightUpperArm: [0.25, -0.1, 0.45],
        rightLowerArm: [1.0],
        leftUpperLeg: [0.55, 0, 0.1],
        leftLowerLeg: [0.5],
        rightUpperLeg: [-0.3, 0, 0.1],
        rightLowerLeg: [0.3],
      },
      root: { lunge: 0.32, hop: -0.05 },
    },
    { t: 0.5, ease: 'out' },
  ],
  trails: [{ from: 0.06, to: 0.26, hand: 'left', color: HOLY, tail: HOLY_TAIL, style: 'holy', width: 1, fade: 0.26, alpha: 0.75 }],
  cues: [
    { t: 0.2, fx: 'impact', at: 'front', color: HOLY, scale: 0.9 },
    { t: 0.2, fx: 'holy', at: 'hand', hand: 'left', color: GOLD, scale: 1 },
    { t: 0.21, fx: 'dust', at: 'ground', scale: 0.5 },
  ],
  hitStop: { t: 0.2, ms: 40, shake: 0.12 },
});

/** Shield Bash: a very fast shield jab with a white flash (the interrupt). */
export const shieldBash = defineClip({
  id: 'warden.shield_bash',
  duration: 0.4,
  blendIn: 0.03,
  keys: [
    {
      t: 0.05,
      ease: 'out',
      pose: {
        spine: [0.05, -0.3, 0],
        head: [0, 0.25, 0],
        leftUpperArm: [0.4, 0, 0.4],
        leftLowerArm: [1.2],
      },
      root: { lunge: -0.04 },
    },
    {
      t: 0.11,
      ease: 'snap',
      hold: 0.04,
      pose: {
        spine: [0.18, 0.4, 0],
        chest: [0.05, 0.15, 0],
        head: [-0.05, -0.35, 0],
        leftUpperArm: [1.15, 0.65, 0],
        leftLowerArm: [0],
        rightUpperArm: [-0.15, -0.1, 0.4],
        rightLowerArm: [0.7],
        leftUpperLeg: [0.35],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.24 },
    },
    { t: 0.4, ease: 'out' },
  ],
  trails: [{ from: 0.04, to: 0.17, hand: 'left', color: WHITE, tail: STEEL_TAIL, style: 'streak', width: 1, fade: 0.14, alpha: 0.9 }],
  cues: [
    { t: 0.11, fx: 'flash', at: 'hand', hand: 'left', color: WHITE, scale: 1 },
    { t: 0.11, fx: 'impact', at: 'front', color: WHITE, scale: 0.8 },
    { t: 0.12, fx: 'sparks', at: 'hand', hand: 'left', color: STEEL, scale: 0.6 },
  ],
  hitStop: { t: 0.11, ms: 40, shake: 0.1 },
});

/** Taunt: chest out, sword pointed at the target, head thrown back in a roar, a stamp. */
export const taunt = defineClip({
  id: 'warden.taunt',
  duration: 0.62,
  blendIn: 0.05,
  keys: [
    // Draw in a breath: sword pulled back by the shoulder, chest swelling.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [-0.08, -0.25, 0],
        chest: [-0.1, 0, 0],
        head: [0.1, 0.2, 0],
        rightUpperArm: [1.0, -0.2, 0.6],
        rightLowerArm: [1.3],
        leftUpperArm: [0.2, -0.1, 0.5],
        leftLowerArm: [0.5],
        rightUpperLeg: [0.2],
        rightLowerLeg: [0.35],
      },
      root: { hop: 0.05 },
    },
    // Point and roar: the blade thrust out straight at the target, shield arm flung wide,
    // chest open, head back, the lead foot stamping down.
    {
      t: 0.2,
      ease: 'back',
      overshoot: 1.4,
      hold: 0.16,
      pose: {
        spine: [-0.15, 0.15, 0],
        chest: [-0.15, 0.1, 0],
        neck: [-0.1, 0, 0],
        head: [-0.3, -0.15, 0],
        rightUpperArm: [1.5, 0.35, 0.05],
        rightLowerArm: [0.02],
        // Wrist yaw levels the blade so it points straight at the target (not up past the head).
        rightHand: [0, -1.4, 0],
        leftUpperArm: [0.3, -0.25, 1.0],
        leftLowerArm: [0.4],
        leftUpperLeg: [0.35, 0, 0.12],
        leftLowerLeg: [0.35],
        rightUpperLeg: [-0.15, 0, 0.12],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.12, hop: -0.04 },
    },
    { t: 0.62, ease: 'out' },
  ],
  trails: [{ from: 0.1, to: 0.26, hand: 'right', color: ROAR, tail: ROAR_TAIL, style: 'streak', width: 0.8, fade: 0.2, alpha: 0.85 }],
  cues: [
    { t: 0.2, fx: 'flash', at: 'tip', color: ROAR, scale: 0.7 },
    { t: 0.2, fx: 'shockwave', at: 'self', color: ROAR, scale: 0.8 },
    { t: 0.21, fx: 'dust', at: 'ground', scale: 0.6 },
  ],
  hitStop: { t: 0.2, ms: 35, shake: 0.12 },
});

/** Grip: reach out with the shield hand, clench, then yank it back with a step away. */
export const grip = defineClip({
  id: 'warden.grip',
  duration: 0.6,
  blendIn: 0.05,
  keys: [
    // Reach: lean in, left arm flung out at the target.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        spine: [0.2, 0.35, 0],
        chest: [0.05, 0.15, 0],
        head: [-0.05, -0.3, 0],
        leftUpperArm: [1.45, 0.4, 0.2],
        leftLowerArm: [0.05],
        rightUpperArm: [0.1, -0.15, 0.5],
        rightLowerArm: [0.7],
        leftUpperLeg: [0.4],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.14 },
    },
    // Clench (a short hold at full reach).
    {
      t: 0.2,
      ease: 'linear',
      pose: {
        spine: [0.22, 0.38, 0],
        chest: [0.05, 0.15, 0],
        head: [-0.05, -0.3, 0],
        leftUpperArm: [1.5, 0.4, 0.2],
        leftLowerArm: [0.15],
        rightUpperArm: [0.1, -0.15, 0.5],
        rightLowerArm: [0.7],
        leftUpperLeg: [0.4],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.16 },
    },
    // Yank: torso wrenches right, elbow driven back past the ribs, weight thrown onto the back foot.
    {
      t: 0.3,
      ease: 'snap',
      hold: 0.06,
      pose: {
        spine: [-0.1, -0.45, 0],
        chest: [-0.05, -0.15, 0],
        head: [0.05, 0.4, 0],
        leftUpperArm: [0.1, -0.2, 0.55],
        leftLowerArm: [1.6],
        rightUpperArm: [0.5, 0, 0.6],
        rightLowerArm: [0.6],
        leftUpperLeg: [0.15, 0, 0.1],
        leftLowerLeg: [0.15],
        rightUpperLeg: [0.35, 0, 0.1],
        rightLowerLeg: [0.65],
      },
      root: { lunge: -0.2, hop: -0.05 },
    },
    { t: 0.6, ease: 'out' },
  ],
  trails: [
    { from: 0.06, to: 0.18, hand: 'left', color: CHAIN, tail: CHAIN_TAIL, style: 'streak', width: 1, fade: 0.16, alpha: 0.7 },
    { from: 0.2, to: 0.36, hand: 'left', color: CHAIN, tail: CHAIN_TAIL, style: 'streak', width: 1, fade: 0.2, alpha: 0.85 },
  ],
  cues: [
    { t: 0.2, fx: 'flash', at: 'hand', hand: 'left', color: CHAIN, scale: 0.6 },
    { t: 0.3, fx: 'embers', at: 'hand', hand: 'left', color: CHAIN, scale: 0.8 },
    { t: 0.31, fx: 'dust', at: 'ground', scale: 0.5 },
  ],
  hitStop: { t: 0.3, ms: 35, shake: 0.1 },
});

// ---------------------------------------------------------------------------------------------
// Rotation rework: Aegis Throw (5), Aegis Recall (key-5 transform, off-GCD), Riposte (6).
// ---------------------------------------------------------------------------------------------

const AEGIS = '#ffd76a';
const AEGIS_TAIL = '#8a5a12';
const SILVER = '#dfe8ff';
const RIPOSTE = '#9fb6e8';
const RIPOSTE_TAIL = '#2c3c6e';

/**
 * Aegis Throw: a backhand discus throw. The shield arm folds across the chest to the right
 * shoulder while the torso coils right, then whips out wide to the left with a long step; the
 * spell fx flies the golden aegis from the hand.
 */
export const aegisThrow = defineClip({
  id: 'warden.aegis_throw',
  duration: 0.62,
  blendIn: 0.04,
  keys: [
    // Coil: shield arm wrapped across the body, torso turned right, weight on the back foot.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        spine: [0.1, -0.6, 0.05],
        chest: [0.04, -0.25, 0],
        head: [0, 0.55, 0],
        leftUpperArm: [0.95, 1.15, 0.15],
        leftLowerArm: [1.55],
        leftHand: [0, 0, 0.4],
        rightUpperArm: [0.2, -0.2, 0.6],
        rightLowerArm: [0.7],
        rightUpperLeg: [0.3, 0, 0.1],
        rightLowerLeg: [0.5],
        leftUpperLeg: [-0.05, 0, 0.1],
        leftLowerLeg: [0.2],
      },
      root: { lunge: -0.12, hop: -0.04, spin: -0.04 },
    },
    // Release: the torso unwinds left, the arm snaps out flat (the throw), long stride in.
    {
      t: 0.24,
      ease: 'snap',
      hold: 0.06,
      pose: {
        spine: [0.16, 0.55, -0.08],
        chest: [0.05, 0.22, 0],
        head: [-0.05, -0.35, 0],
        leftUpperArm: [1.4, -0.2, 0.5],
        leftLowerArm: [0.05],
        leftHand: [0, 0, -0.5],
        rightUpperArm: [-0.2, -0.15, 0.55],
        rightLowerArm: [0.85],
        leftUpperLeg: [0.6, 0, 0.08],
        leftLowerLeg: [0.45],
        rightUpperLeg: [-0.35, 0, 0.08],
        rightLowerLeg: [0.25],
      },
      root: { lunge: 0.34, hop: -0.04, spin: 0.04 },
    },
    // Follow-through: the arm stays pointing after the shield, the body settles.
    {
      t: 0.42,
      ease: 'out',
      pose: {
        spine: [0.1, 0.35, 0],
        head: [-0.05, -0.25, 0],
        leftUpperArm: [1.2, -0.1, 0.45],
        leftLowerArm: [0.2],
        rightUpperArm: [0.05, -0.15, 0.5],
        rightLowerArm: [0.8],
        leftUpperLeg: [0.4, 0, 0.08],
        leftLowerLeg: [0.35],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.2 },
    },
    { t: 0.62, ease: 'out' },
  ],
  trails: [
    { from: 0.12, to: 0.32, hand: 'left', color: AEGIS, tail: AEGIS_TAIL, style: 'crescent', width: 1, fade: 0.26, alpha: 0.9 },
    { from: 0.16, to: 0.34, hand: 'left', color: HOLY, tail: HOLY_TAIL, style: 'holy', width: 0.7, fade: 0.34, alpha: 0.55 },
  ],
  cues: [
    { t: 0.24, fx: 'flash', at: 'hand', hand: 'left', color: HOLY, scale: 0.9 },
    { t: 0.24, fx: 'holy', at: 'front', color: AEGIS, scale: 0.9 },
    { t: 0.25, fx: 'sparks', at: 'hand', hand: 'left', color: GOLD, scale: 0.7 },
    { t: 0.26, fx: 'dust', at: 'ground', scale: 0.5 },
  ],
  hitStop: { t: 0.24, ms: 40, shake: 0.12 },
});

/**
 * Aegis Recall (off-GCD): the open shield hand flung out to call the aegis home, then the catch:
 * the arm snaps back across the chest and the whole body is shoved back half a step by the
 * impact. Bright gold catch flash.
 */
export const aegisRecall = defineClip({
  id: 'warden.aegis_recall',
  duration: 0.48,
  blendIn: 0.03,
  keys: [
    // Call: lean in, shield arm straight out at the target, palm open.
    {
      t: 0.1,
      ease: 'out',
      pose: {
        spine: [0.16, 0.3, 0],
        chest: [0.05, 0.1, 0],
        head: [-0.05, -0.25, 0],
        leftUpperArm: [1.55, 0.25, 0.15],
        leftLowerArm: [0],
        leftHand: [0, 0, -0.7],
        rightUpperArm: [0.1, -0.15, 0.55],
        rightLowerArm: [0.75],
        leftUpperLeg: [0.3, 0, 0.05],
        leftLowerLeg: [0.25],
      },
      root: { lunge: 0.1 },
    },
    // Catch: the shield slams home across the chest, knees buckle, pushed back by the impact.
    {
      t: 0.2,
      ease: 'snap',
      hold: 0.06,
      pose: {
        spine: [-0.12, 0.1, 0],
        chest: [-0.06, 0.05, 0],
        head: [0.08, -0.1, 0],
        leftUpperArm: [0.95, 0.8, 0.15],
        leftLowerArm: [0.95],
        rightUpperArm: [0.35, -0.2, 0.75],
        rightLowerArm: [0.55],
        leftUpperLeg: [0.35, 0, 0.2],
        rightUpperLeg: [0.45, 0, 0.2],
        leftLowerLeg: [0.6],
        rightLowerLeg: [0.75],
      },
      root: { lunge: -0.22, hop: -0.08 },
    },
    { t: 0.48, ease: 'out' },
  ],
  trails: [
    { from: 0.12, to: 0.28, hand: 'left', color: AEGIS, tail: AEGIS_TAIL, style: 'streak', width: 1, fade: 0.18, alpha: 0.9 },
    { from: 0.14, to: 0.3, hand: 'left', color: WHITE, tail: GOLD, style: 'holy', width: 0.6, fade: 0.26, alpha: 0.5 },
  ],
  cues: [
    { t: 0.2, fx: 'impact', at: 'hand', hand: 'left', color: AEGIS, scale: 1 },
    { t: 0.2, fx: 'flash', at: 'self', color: HOLY, scale: 0.8 },
    { t: 0.21, fx: 'sparks', at: 'hand', hand: 'left', color: GOLD, scale: 0.8 },
    { t: 0.22, fx: 'dust', at: 'ground', scale: 0.45 },
  ],
  hitStop: { t: 0.2, ms: 35, shake: 0.12 },
});

/**
 * Riposte: a parry, then the answer. The shield snaps up to turn a blow aside while the sword
 * cocks back by the right hip, then one flat, wide sweep across the frontal cone with a darting
 * step and a little side hop (silver-blue fan, no body turn: this is the anti-Cleave).
 */
export const riposte = defineClip({
  id: 'warden.riposte',
  duration: 0.56,
  blendIn: 0.03,
  keys: [
    // Parry: shield flicked up and out to the left, sword drawn back low on the right, lean back.
    {
      t: 0.09,
      ease: 'snap',
      pose: {
        spine: [-0.1, -0.35, 0.05],
        chest: [-0.05, -0.15, 0],
        head: [0.05, 0.3, 0],
        leftUpperArm: [1.6, 0.35, 0.55],
        leftLowerArm: [0.9],
        rightUpperArm: [0.1, -0.5, 0.9],
        rightLowerArm: [0.45],
        rightUpperLeg: [0.25, 0, 0.12],
        rightLowerLeg: [0.45],
        leftLowerLeg: [0.15],
      },
      root: { lunge: -0.12, side: 0.06 },
    },
    // The sweep: the blade fans flat across the front from right to left, a step in and to the left.
    {
      t: 0.2,
      ease: 'back',
      overshoot: 1.2,
      hold: 0.05,
      pose: {
        spine: [0.22, 0.6, 0],
        chest: [0.06, 0.22, 0],
        head: [-0.08, -0.45, 0],
        rightUpperArm: [1.05, 1.15, 0.45],
        rightLowerArm: [0.05],
        rightHand: [0, 0, 0.35],
        leftUpperArm: [0.3, -0.3, 0.8],
        leftLowerArm: [0.85],
        leftUpperLeg: [0.55, 0, 0.12],
        leftLowerLeg: [0.5],
        rightUpperLeg: [-0.3, 0, 0.12],
        rightLowerLeg: [0.3],
      },
      root: { lunge: 0.32, side: -0.12, hop: -0.04 },
    },
    {
      t: 0.36,
      ease: 'out',
      pose: {
        spine: [0.12, 0.35, 0],
        head: [-0.05, -0.25, 0],
        rightUpperArm: [0.7, 0.8, 0.5],
        rightLowerArm: [0.3],
        leftUpperArm: [0.3, -0.2, 0.6],
        leftLowerArm: [0.9],
        leftUpperLeg: [0.35, 0, 0.1],
        leftLowerLeg: [0.35],
      },
      root: { lunge: 0.18, side: -0.06 },
    },
    { t: 0.56, ease: 'out' },
  ],
  trails: [
    { from: 0.08, to: 0.27, hand: 'right', color: SILVER, tail: RIPOSTE_TAIL, style: 'crescent', width: 1, fade: 0.26, alpha: 0.9 },
    { from: 0.1, to: 0.26, hand: 'right', color: RIPOSTE, tail: RIPOSTE_TAIL, style: 'streak', width: 0.55, fade: 0.18, alpha: 0.7 },
    { from: 0.03, to: 0.12, hand: 'left', color: STEEL, tail: STEEL_TAIL, style: 'streak', width: 0.8, fade: 0.12, alpha: 0.6 },
  ],
  cues: [
    { t: 0.09, fx: 'sparks', at: 'hand', hand: 'left', color: WHITE, scale: 0.6 },
    { t: 0.2, fx: 'slash', at: 'front', color: SILVER, scale: 1.5 },
    { t: 0.2, fx: 'sparks', at: 'tip', color: GOLD, scale: 0.7 },
    { t: 0.21, fx: 'dust', at: 'ground', scale: 0.5 },
  ],
  hitStop: { t: 0.2, ms: 45, shake: 0.14 },
});

/** Ability id → its strike clip. Every warden ability is instant, so each has only a strike. */
export const WARDEN_KIT: Readonly<Record<keyof typeof ABILITY.warden, Clip>> = {
  shieldSlam,
  cleaveStrike,
  bulwarkStrike,
  consecratedGround,
  lastStand,
  shieldWallBash,
  shieldBash,
  taunt,
  grip,
  aegisThrow,
  riposte,
  aegisRecall,
};

export function registerWardenKit(): void {
  for (const key of Object.keys(WARDEN_KIT) as (keyof typeof ABILITY.warden)[]) {
    registerAbilityClips(ABILITY.warden[key], { strike: WARDEN_KIT[key] });
  }
}
